/**
 * Purchase Log: editable table of actual BTC purchases.
 *
 * Mirrors the workbook's Purchase Log columns B..G (date, USD, BTC,
 * effective price, running balance, cost basis). Effective price,
 * running balance, and cost basis are derived (read-only) using
 * engine functions.
 *
 * AE/AF additions:
 *   - "Loan" column on each purchase row, set when the buy was
 *     loop-loan funded.
 *   - Bottom section "Loop loan events" with borrow / repayment /
 *     balance-override timeline.
 *
 * Patch I changes: USD to whole dollars, cost basis to whole dollars,
 *   minWidth on every column, em-dashes scrubbed.
 *
 * Patch J changes:
 *   - Tighter column minWidths so the table fits inside the card on
 *     typical viewports (was overflowing past the right edge in
 *     pre-J). Loan column especially narrowed per user spec.
 *   - Loan-funded row tint switched from the pre-Patch-J browned
 *     orange rgba tints to the new --accent-tint-* CSS vars driven
 *     by --accent. Now any future change to the bitcoin accent
 *     color cascades automatically instead of leaving stale orange
 *     behind.
 *
 * Patch J.1: conditional spread on DecimalInput style prop to
 *   satisfy exactOptionalPropertyTypes (passing style={undefined}
 *   differs from omitting the prop).
 *
 * Patch K: comment cleanup so the codebase grep for stale color
 *   literals stays clean. Functional code unchanged from J.1.
 *
 * Patch BB:
 *   - Section title renamed from "Actual transactions" to
 *     "Bitcoin transactions" so it parallels the ETF section's
 *     "Bitcoin ETF positions" naming. Same data, clearer label.
 *   - AutoBuyPanel rendered above the table, configures the BTC
 *     auto-buy schedule. When enabled, the walker creates rows
 *     priced at today's BTC price on each scheduled date.
 *   - AUTO badge in the Date cell for rows the walker created
 *     (autoCreated === true). The badge disappears as soon as the
 *     user edits ANY field on the row, because the store's
 *     updatePurchase action clears the flag on any patch.
 */

import { useState, useMemo } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import {
  effectivePurchasePrice,
  runningBtcBalance,
  cumulativeUsdInvested,
} from '../engine/purchases.js';
import { computeLoanState } from '../engine/loan-state.js';
import { formatBtc, formatUsd, formatPct } from '../components/Money.js';
import { DecimalInput } from '../components/DecimalInput.js';
import { EtfTransactions } from '../components/EtfTransactions.js';
import { SecuritiesCard } from '../components/SecuritiesCard.js';
import { RealEstateCard } from '../components/RealEstateCard.js';
import { CashCard } from '../components/CashCard.js';
import { DebtCard } from '../components/DebtCard.js';
import { AutoBuyPanel } from '../components/AutoBuyPanel.js';
// Patch BE: provider picker + manual APR override let the user pick
// Aave vs Ledn and (optionally) pin a manual APR that overrides both.
import { LoanProviderPicker } from '../components/LoanProviderPicker.js';
import type { LoanEventKind } from '../engine/types.js';

export function PurchaseLog(): JSX.Element {
  const purchases = useAppStore(s => s.purchases);
  const addPurchase = useAppStore(s => s.addPurchase);
  const updatePurchase = useAppStore(s => s.updatePurchase);
  const removePurchase = useAppStore(s => s.removePurchase);

  // Loan-related store hooks
  const loanEvents = useAppStore(s => s.loanEvents);
  const addLoanEvent = useAppStore(s => s.addLoanEvent);
  const updateLoanEvent = useAppStore(s => s.updateLoanEvent);
  const removeLoanEvent = useAppStore(s => s.removeLoanEvent);

  // Patch BE: effective borrow APR comes from a four-tier priority:
  //   1. manualBorrowAprOverride - user explicitly typed a rate into the
  //      Active APR tile. Highest precedence; nothing else displaces it
  //      until cleared.
  //   2. Selected provider's live snapshot - Aave (via DefiLlama) or
  //      Ledn (hardcoded from io/live/ledn.ts).
  //   3. The other provider's snapshot - covers the edge case where the
  //      user just switched provider but the new provider's snapshot
  //      hasn't loaded yet on this page; better to show the OTHER
  //      provider's rate than collapse to 6%.
  //   4. 6% hardcoded floor - last-resort default if both snapshots
  //      are null (offline + Ledn module load error, very unlikely).
  //
  // Pre-BE: the only path was aaveSnapshot?.borrowApr ?? 0.06, which
  // produced the "Active APR shows 6% even when Aave is different"
  // bug whenever the user landed on this page before LoopLoan had
  // populated aaveSnapshot via its polling hook.
  const selectedProvider = useAppStore(s => s.selectedLoanProvider);
  const aaveSnapshot = useAppStore(s => s.aaveSnapshot);
  const lednSnapshot = useAppStore(s => s.lednSnapshot);
  const manualOverride = useAppStore(s => s.manualBorrowAprOverride);
  const setManualOverride = useAppStore(s => s.setManualBorrowAprOverride);

  const activeProviderSnap = selectedProvider === 'aave' ? aaveSnapshot : lednSnapshot;
  const fallbackProviderSnap = selectedProvider === 'aave' ? lednSnapshot : aaveSnapshot;
  const effectiveBorrowApr =
    manualOverride
    ?? activeProviderSnap?.borrowApr
    ?? fallbackProviderSnap?.borrowApr
    ?? new Decimal('0.06');

  // Derived columns (engine)
  const balances = runningBtcBalance(purchases);
  const cumUsd = cumulativeUsdInvested(purchases);
  // Patch DA: costBases removed - the Cost basis per-row column was dropped
  // and weightedAverageCostBasis isn't used elsewhere on this page. Engine
  // function itself preserved for other potential consumers.

  // Loan state for summary card
  const loanState = useMemo(() => computeLoanState({
    purchases,
    loanEvents,
    asOf: new Date(),
    fallbackApr: effectiveBorrowApr,
  }), [purchases, loanEvents, effectiveBorrowApr]);

  // currentAaveApr is still used as the placeholder/default for newly-
  // added loan event APR snapshots and the loanAprAtTx stamping on
  // purchases. Wired to effectiveBorrowApr so it tracks provider
  // selection + manual override consistently with the tile.
  const currentAaveApr = effectiveBorrowApr;

  // New-row form state
  // Patch CU: sv-SE locale gives YYYY-MM-DD format in the user's local
  // timezone. The naive toISOString().slice(0,10) returns UTC date,
  // which for users west of UTC during evening hours shows tomorrow's
  // date as the default (Paul reported this for Central time evenings).
  // Same fix applied to ETF + Securities transaction defaults and the
  // loan event date default a few lines below.
  const [newDate, setNewDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [newUsd, setNewUsd] = useState('');
  const [newBtc, setNewBtc] = useState('');
  const [newLoanFunded, setNewLoanFunded] = useState('');

  // Patch BL: open-state mirrors for the two Bitcoin disclosure
  // sections. The section-title CSS class strips the native marker on
  // <summary>, so we render an explicit rotating chevron driven by
  // onToggle. Default is closed (false), matching the previous default
  // collapsed state of the wrapped <details>.
  const [bitcoinEtfsOpen, setBitcoinEtfsOpen] = useState(false);
  const [bitcoinLoanEventsOpen, setBitcoinLoanEventsOpen] = useState(false);

  const canAdd = newUsd && newBtc && Number(newUsd) > 0 && Number(newBtc) > 0;

  const handleAdd = (): void => {
    if (!canAdd) return;
    const loanFunded = newLoanFunded ? new Decimal(newLoanFunded) : null;
    addPurchase({
      date: new Date(newDate + 'T12:00:00Z'),
      usd: new Decimal(newUsd),
      btc: new Decimal(newBtc),
      category: loanFunded && loanFunded.greaterThan(0) ? 'loan' : 'manual',
      source: 'actual',
      ...(loanFunded && loanFunded.greaterThan(0)
        ? { loanFundedUsd: loanFunded, loanAprAtTx: currentAaveApr }
        : {}),
    });
    setNewUsd('');
    setNewBtc('');
    setNewLoanFunded('');
  };

  // ---- Loan events form state ----
  const [evDate, setEvDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [evKind, setEvKind] = useState<LoanEventKind>('borrow');
  const [evAmount, setEvAmount] = useState('');
  const [evApr, setEvApr] = useState(''); // % string, e.g. "5.73"
  const [evNote, setEvNote] = useState('');

  const canAddEvent = evAmount && Number(evAmount) > 0;

  const handleAddEvent = (): void => {
    if (!canAddEvent) return;
    const aprDecimal = evApr ? new Decimal(evApr).div(100) : null;
    // For borrow/override, default the APR to the current Aave snapshot if not specified.
    const useApr = aprDecimal ?? (evKind === 'borrow' || evKind === 'balance-override' ? currentAaveApr : null);

    addLoanEvent({
      date: new Date(evDate + 'T12:00:00Z'),
      kind: evKind,
      amountUsd: new Decimal(evAmount),
      ...(useApr !== null ? { aprSnapshot: useApr } : {}),
      ...(evNote ? { note: evNote } : {}),
    });
    setEvAmount('');
    setEvApr('');
    setEvNote('');
  };

  // Sort events oldest-first for display.
  const sortedEvents = useMemo(
    () => [...loanEvents].sort((a, b) => a.date.getTime() - b.date.getTime()),
    [loanEvents],
  );

  return (
    <>
      <section className="section">
        <p className="section-eyebrow">Purchases</p>
        {/* Patch BB: renamed from "Actual transactions" to match the ETF
            section's "Bitcoin ETF positions" naming, and to be unambiguous
            about what the table holds. */}
        <h2 className="section-title">Bitcoin transactions</h2>
        <p className="muted" style={{ marginTop: 'calc(-1 * var(--gap-m))', marginBottom: 'var(--gap-m)' }}>
          Edit any field inline. Effective price, running balance, and cost basis recompute automatically.
          Set <strong>Loan</strong> on a row to mark the buy as loop-loan funded.
        </p>

        {/* Patch BB: BTC auto-buy panel. Sits above the transaction table
            so it reads as "schedule rules" before the "transaction history."
            Walker creates rows below as scheduled dates pass; user can
            review the AUTO-tagged rows and edit any of them to clear the
            tag. */}
        <AutoBuyPanel
          assetType="btc"
          assetId="btc"
          assetLabel="BTC (direct purchases)"
        />

        <table className="tbl">
          <thead>
            <tr>
              {/* Patch J: tighter column widths so the table fits inside
                  the card. Sum of mins ~590px + 9 x 16px padding ~ 734px,
                  vs. card content width ~1116px on full desktop and
                  ~960px on 1024w. Loan column especially narrow per
                  user spec since most rows have it blank ("-"). */}
              <th style={{ minWidth: 105 }}>Date</th>
              <th className="num" style={{ minWidth: 80 }}>USD</th>
              <th className="num" style={{ minWidth: 100 }}>BTC</th>
              <th className="num" style={{ minWidth: 55 }} title="USD funded by loop loan (subset of total USD). Stamps the current Aave APR.">Loan</th>
              <th className="num" style={{ minWidth: 110 }}>Effective price</th>
              {/* Patch DA: Cost basis column removed for visual cleanup.
                  weightedAverageCostBasis was only used here for the per-row
                  Cost basis display; no other page (Dashboard, NetWorth)
                  references it as a per-row array. Engine function preserved
                  for completeness; just no longer imported.
                  Column widths re-balanced so the Loan column (which usually
                  shows just "-") doesn't visually dominate now that there's
                  more horizontal room. */}
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 ? (
              <tr>
                <td colSpan={6} className="dim" style={{ textAlign: 'center', padding: 'var(--gap-l)' }}>
                  No purchases yet. Use the row below to add one.
                </td>
              </tr>
            ) : (
              purchases.map((p, i) => {
                const eff = effectivePurchasePrice(p.usd, p.btc);
                const isLoanFunded = !!p.loanFundedUsd && p.loanFundedUsd.greaterThan(0);
                return (
                  <tr
                    key={p.id}
                    className="actual"
                    style={isLoanFunded ? { background: 'var(--accent-tint-06)' } : undefined}
                  >
                    <td>
                      {/* Patch BB: AUTO badge sits above the date input when
                          autoCreated is true. Editing ANY cell clears it
                          (the store's updatePurchase strips autoCreated on
                          any patch). */}
                      {p.autoCreated ? (
                        <div style={{
                          display: 'inline-block',
                          fontSize: 9,
                          padding: '1px 5px',
                          marginBottom: 2,
                          background: 'var(--accent)',
                          color: 'var(--bg-deep)',
                          fontWeight: 700,
                          fontFamily: 'var(--mono)',
                          borderRadius: 2,
                          letterSpacing: '0.05em',
                        }} title="Created by auto-buy schedule. Editing any field on this row removes the tag.">
                          AUTO
                        </div>
                      ) : null}
                      <input
                        type="date"
                        className="left"
                        value={p.date.toISOString().slice(0, 10)}
                        onChange={e => updatePurchase(p.id, { date: new Date(e.target.value + 'T12:00:00Z') })}
                      />
                    </td>
                    <td className="num">
                      <DecimalInput
                        step="1"
                        decimals={0}
                        value={p.usd}
                        onChange={d => updatePurchase(p.id, { usd: d })}
                      />
                    </td>
                    <td className="num">
                      <DecimalInput
                        step="0.00000001"
                        decimals={8}
                        value={p.btc}
                        onChange={d => updatePurchase(p.id, { btc: d })}
                      />
                    </td>
                    <td className="num">
                      <DecimalInput
                        step="100"
                        decimals={2}
                        placeholder="-"
                        value={p.loanFundedUsd}
                        onChange={d => updatePurchase(p.id, {
                          loanFundedUsd: d,
                          ...(p.loanAprAtTx ? {} : { loanAprAtTx: currentAaveApr }),
                          ...(p.category === 'manual' ? { category: 'loan' } : {}),
                        })}
                        onClear={() => updatePurchase(p.id, {
                          loanFundedUsd: undefined,
                          loanAprAtTx: undefined,
                          ...(p.category === 'loan' ? { category: 'manual' } : {}),
                        })}
                        title={p.loanAprAtTx ? `APR snapshot at tx: ${p.loanAprAtTx.times(100).toFixed(2)}%` : 'No loan'}
                        // exactOptionalPropertyTypes: passing style={undefined}
                        // differs from omitting the prop. Spread conditionally
                        // so the prop is fully omitted on cash-funded rows.
                        {...(isLoanFunded ? { style: { color: 'var(--accent)' } } : {})}
                      />
                    </td>
                    <td className="num dim">
                      {/* Patch DY/DZ: compact eff-price format. Under
                          $999.95k renders as $X.Xk (one decimal of
                          thousands); $1M and up rolls over to $X.Xm
                          (one decimal of millions). Threshold is
                          999950 rather than 1000000 so a value like
                          $999,999 - which would otherwise toFixed(1)
                          to the awkward $1000.0k - crosses over to
                          $1.0m cleanly. Title attr keeps the precise
                          value available on hover for the exact
                          figure. */}
                      {eff
                        ? <span title={`$${formatUsd(eff, { decimals: 2 })}`}>
                            {eff.gte(999_950)
                              ? `$${eff.div(1_000_000).toFixed(1)}m`
                              : `$${eff.div(1_000).toFixed(1)}k`}
                          </span>
                        : '-'}
                    </td>
                    <td>
                      <button
                        className="row-action"
                        onClick={() => {
                          if (confirm('Delete this purchase?')) removePurchase(p.id);
                        }}
                        title="Delete row"
                      >
                        {'\u2715'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
            {/* New row */}
            <tr>
              <td>
                <input
                  type="date"
                  className="left"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                />
              </td>
              <td className="num">
                <input
                  type="number"
                  step="1"
                  placeholder="0"
                  value={newUsd}
                  onChange={e => setNewUsd(e.target.value)}
                />
              </td>
              <td className="num">
                <input
                  type="number"
                  step="0.00000001"
                  placeholder="0.00000000"
                  value={newBtc}
                  onChange={e => setNewBtc(e.target.value)}
                />
              </td>
              <td className="num">
                <input
                  type="number"
                  step="100"
                  placeholder="-"
                  value={newLoanFunded}
                  onChange={e => setNewLoanFunded(e.target.value)}
                  title="USD funded by loop loan. Leave blank for cash-funded."
                />
              </td>
              {/* Patch DA: now 1 dash since Cost basis column dropped.
                  Effective price stays computed at render. */}
              <td className="num dim">-</td>
              <td>
                <button
                  className="primary"
                  onClick={handleAdd}
                  disabled={!canAdd}
                  style={{ padding: '4px 10px', fontSize: 'var(--fs-micro)' }}
                >
                  ADD
                </button>
              </td>
            </tr>
          </tbody>
          {purchases.length > 0 ? (
            <tfoot>
              <tr>
                <td><strong className="serif" style={{ fontSize: 'var(--fs-lead)' }}>Total</strong></td>
                <td className="num">
                  <strong>${formatUsd(cumUsd[cumUsd.length - 1] ?? ZERO, { decimals: 0 })}</strong>
                </td>
                <td className="num">
                  <strong>{formatBtc(balances[balances.length - 1] ?? ZERO, 3)}</strong>
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>

      {/* RETIREMENT PLAN HOLDINGS / ETF TRANSACTIONS

          Patch BK: wrapped in a disclosure so the Bitcoin ETFs
          section is collapsed by default. Bitcoin transactions stays
          visible/expanded above (it's the primary thing users hit);
          this keeps Purchases scannable without losing access.

          Patch BP: converted from <details>/<summary> to a controlled
          button-based disclosure. The <details> approach left WebKit's
          ::-webkit-details-marker visible on Chrome/Edge despite the
          section-title class stripping the Firefox marker, which read
          as a duplicate arrow above the eyebrow (the screenshot Paul
          flagged). A controlled disclosure renders exactly the chevron
          we draw and nothing else. */}
      <section className="section">
        <button
          type="button"
          onClick={() => setBitcoinEtfsOpen(o => !o)}
          aria-expanded={bitcoinEtfsOpen}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', textAlign: 'left', width: '100%',
            color: 'inherit', font: 'inherit',
          }}
        >
          <div className="section-title">
            <span className="section-eyebrow" style={{ display: 'block' }}>Bitcoin ETFs</span>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                marginRight: '0.4em',
                transition: 'transform 0.15s',
                transform: bitcoinEtfsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                color: 'var(--text-soft)',
              }}
            >
              {'\u25B8'}
            </span>
            Bitcoin ETF positions
          </div>
        </button>
        {bitcoinEtfsOpen ? (
          <div style={{ marginTop: 'var(--gap-m)' }}>
            <EtfTransactions />
          </div>
        ) : null}
      </section>

      {/* LOOP-LOAN EVENTS

          Patch BK: renamed "Loan events" to "Bitcoin loan events" since
          we're about to introduce non-Bitcoin debt entries in the next
          patch (BL's Debt sub-card), and the unqualified name would be
          ambiguous. Collapsed by default, matching Bitcoin ETFs above.

          Patch BP: converted from <details>/<summary> to a controlled
          button-based disclosure (same WebKit marker fix applied to
          Bitcoin ETFs above; see comment there). */}
      <section className="section">
        <button
          type="button"
          onClick={() => setBitcoinLoanEventsOpen(o => !o)}
          aria-expanded={bitcoinLoanEventsOpen}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', textAlign: 'left', width: '100%',
            color: 'inherit', font: 'inherit',
          }}
        >
          <div className="section-title">
            <span className="section-eyebrow" style={{ display: 'block' }}>Loop loan</span>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                marginRight: '0.4em',
                transition: 'transform 0.15s',
                transform: bitcoinLoanEventsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                color: 'var(--text-soft)',
              }}
            >
              {'\u25B8'}
            </span>
            Bitcoin loan events
          </div>
        </button>
        {bitcoinLoanEventsOpen ? (
          <div style={{ marginTop: 'var(--gap-m)' }}>
        <p className="muted" style={{ marginBottom: 'var(--gap-m)', lineHeight: 1.6 }}>
          The loan balance shown on the Dashboard is computed from this list plus
          any purchases above with a <strong>Loan</strong> amount set. Three event
          kinds: <code>borrow</code> adds principal, <code>repayment</code>{' '}
          subtracts, <code>balance-override</code> pins the balance to an exact
          USD amount on a given date (use this to reconcile if the snapshot APR
          drifts from your real Aave or Ledn balance).
        </p>

        {/* Patch BE: provider picker. Renders the same dropdown used on
            Loop Loan / Carry / Salary; state is shared so a change here
            propagates everywhere. Visible above the summary tiles so the
            user sees the rate it picks up before reading Active APR. */}
        <div style={{ marginBottom: 'var(--gap-m)' }}>
          <LoanProviderPicker />
        </div>

        {/* Summary card */}
        <div className="stat-grid" style={{ marginBottom: 'var(--gap-m)' }}>
          <div className="stat">
            <div className="label">Current balance</div>
            <div className="value">${formatUsd(loanState.currentBalance, { decimals: 0 })}</div>
            <div className="sub">
              {loanState.lastEventDate
                ? <>last event {loanState.lastEventDate.toLocaleDateString()}</>
                : <>no events yet</>}
            </div>
          </div>
          {/* Patch BE: Active APR tile is now editable. Click into the
              input to type a manual override; the value is wired to the
              store's manualBorrowAprOverride. Clearing reverts to the
              selected provider's snapshot rate. The "sub" line below
              tells the user where the displayed value came from so the
              precedence (override > provider > 6% floor) is legible. */}
          <div className="stat">
            <div className="label">Active APR</div>
            <div className="value mono" style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              {/* Patch BE: exactOptionalPropertyTypes (Patch J.1 pattern) -
                  passing onClear={undefined} fails TS check because
                  DecimalInputProps types it as required-when-present.
                  Conditional spread only attaches onClear when there's
                  actually a manual override to clear.
                  Patch BF: width bumped 90px -> 140px. The pre-BF 90px
                  was sized for a 2-3 digit BTC %, but Ledn's 11.49% with
                  the trailing "%" symbol overflowed - "11.4" displayed
                  with the "9" clipped, then "%" past the input border.
                  140px comfortably fits five chars at fs-lead. */}
              <DecimalInput
                decimals={2}
                step="0.01"
                placeholder="6.00"
                value={effectiveBorrowApr.times(100)}
                onChange={d => setManualOverride(d.div(100))}
                {...(manualOverride !== null ? { onClear: () => setManualOverride(null) } : {})}
                style={{ width: 140, fontSize: 'inherit', textAlign: 'right' }}
                title="Manual override for the canonical borrow APR. Affects loan-state fallback rate, new event placeholders, and the Loop Loan loanApr in 'live' mode. Backspace-to-clear reverts to the selected provider's rate."
              />
              <span>%</span>
            </div>
            {/* Patch BF: subtitle simplified. The pre-BF "6% fallback
                (live data not loaded)" branch shouted a fallback state
                that looked like an error; in practice it appeared every
                time the user landed on Purchases before LoopLoan had
                populated aaveSnapshot. With App.tsx's BF boot-fetch this
                edge case is mostly gone, and on the rare occasion both
                snapshots are still loading the cleaner approach is
                silence. New branches:
                  - manual override: "manual override · use {provider}"
                  - active provider loaded: "from {provider}"
                  - other provider loaded but active not yet: surfaces the
                    fallback transparently as "from {other} ({active} not loaded)"
                    so the user knows the rate isn't from their selected
                    source but isn't 6% either
                  - neither loaded: empty subtitle (no panicked message) */}
            <div className="sub">
              {manualOverride !== null ? (
                <>
                  manual override
                  {activeProviderSnap ? (
                    <>
                      {' \u00B7 '}
                      <button
                        className="ghost"
                        style={{ padding: '0 4px', fontSize: 'var(--fs-micro)' }}
                        onClick={() => setManualOverride(null)}
                        title="Clear the manual override and use the selected provider's live rate."
                      >
                        use {selectedProvider === 'aave' ? 'Aave' : 'Ledn'} ({formatPct(activeProviderSnap.borrowApr)})
                      </button>
                    </>
                  ) : null}
                </>
              ) : activeProviderSnap ? (
                <>from {selectedProvider === 'aave' ? 'Aave' : 'Ledn'}</>
              ) : fallbackProviderSnap ? (
                <>
                  from {selectedProvider === 'aave' ? 'Ledn' : 'Aave'}
                  <span className="faint"> ({selectedProvider === 'aave' ? 'Aave' : 'Ledn'} not loaded)</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="stat">
            <div className="label">Total borrowed</div>
            <div className="value">${formatUsd(loanState.totalBorrowed, { decimals: 0 })}</div>
            <div className="sub">across all events + purchases</div>
          </div>
          <div className="stat">
            <div className="label">Interest accrued</div>
            <div className="value">${formatUsd(loanState.totalInterestAccrued, { decimals: 0 })}</div>
            <div className="sub">since first borrow</div>
          </div>
        </div>

        <table className="tbl">
          <thead>
            <tr>
              <th>Date</th>
              <th>Kind</th>
              <th className="num">USD</th>
              <th className="num">APR snap.</th>
              <th>Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* Synthetic borrows from purchases (read-only here, edit on the row above). */}
            {purchases
              .filter(p => p.loanFundedUsd && p.loanFundedUsd.greaterThan(0))
              .sort((a, b) => a.date.getTime() - b.date.getTime())
              .map(p => (
                <tr key={`p:${p.id}`} className="dim" style={{ background: 'var(--accent-tint-04)' }}>
                  <td>{p.date.toISOString().slice(0, 10)}</td>
                  <td><span style={{ color: 'var(--accent)' }}>borrow</span> <span className="faint">(from purchase)</span></td>
                  <td className="num">${formatUsd(p.loanFundedUsd ?? ZERO, { decimals: 0 })}</td>
                  <td className="num">{p.loanAprAtTx ? formatPct(p.loanAprAtTx) : '-'}</td>
                  <td className="faint">edit on the purchase row above</td>
                  <td />
                </tr>
              ))}

            {/* Explicit loan events */}
            {sortedEvents.length === 0 && purchases.filter(p => p.loanFundedUsd && p.loanFundedUsd.greaterThan(0)).length === 0 ? (
              <tr>
                <td colSpan={6} className="dim" style={{ textAlign: 'center', padding: 'var(--gap-l)' }}>
                  No loan events yet. Use the row below to log a borrow, repayment, or balance override.
                </td>
              </tr>
            ) : sortedEvents.map(ev => (
              <tr key={ev.id}>
                <td>
                  <input
                    type="date"
                    className="left"
                    value={ev.date.toISOString().slice(0, 10)}
                    onChange={e => updateLoanEvent(ev.id, { date: new Date(e.target.value + 'T12:00:00Z') })}
                  />
                </td>
                <td>
                  <select
                    value={ev.kind}
                    onChange={e => updateLoanEvent(ev.id, { kind: e.target.value as LoanEventKind })}
                  >
                    <option value="borrow">borrow</option>
                    <option value="repayment">repayment</option>
                    <option value="balance-override">balance-override</option>
                  </select>
                </td>
                <td className="num">
                  <DecimalInput
                    step="100"
                    decimals={2}
                    value={ev.amountUsd}
                    onChange={d => updateLoanEvent(ev.id, { amountUsd: d })}
                  />
                </td>
                <td className="num">
                  <DecimalInput
                    step="0.01"
                    decimals={2}
                    placeholder="-"
                    value={ev.aprSnapshot ? ev.aprSnapshot.times(100) : undefined}
                    onChange={d => updateLoanEvent(ev.id, { aprSnapshot: d.div(100) })}
                    onClear={() => updateLoanEvent(ev.id, { aprSnapshot: undefined })}
                    title="APR % at the time of this event. Used for accrual until the next event. Only meaningful for borrow / balance-override."
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="left"
                    placeholder="-"
                    value={ev.note ?? ''}
                    onChange={e => updateLoanEvent(ev.id, { note: e.target.value || undefined })}
                  />
                </td>
                <td>
                  <button
                    className="row-action"
                    onClick={() => {
                      if (confirm('Delete this loan event?')) removeLoanEvent(ev.id);
                    }}
                    title="Delete event"
                  >
                    {'\u2715'}
                  </button>
                </td>
              </tr>
            ))}
            {/* New event row */}
            <tr>
              <td>
                <input
                  type="date"
                  className="left"
                  value={evDate}
                  onChange={e => setEvDate(e.target.value)}
                />
              </td>
              <td>
                <select value={evKind} onChange={e => setEvKind(e.target.value as LoanEventKind)}>
                  <option value="borrow">borrow</option>
                  <option value="repayment">repayment</option>
                  <option value="balance-override">balance-override</option>
                </select>
              </td>
              <td className="num">
                <input
                  type="number"
                  step="100"
                  placeholder="0"
                  value={evAmount}
                  onChange={e => setEvAmount(e.target.value)}
                />
              </td>
              <td className="num">
                <input
                  type="number"
                  step="0.01"
                  placeholder={evKind === 'repayment' ? '-' : `${currentAaveApr.times(100).toFixed(2)}`}
                  value={evApr}
                  onChange={e => setEvApr(e.target.value)}
                  title="Default uses the current Aave APR snapshot."
                />
              </td>
              <td>
                <input
                  type="text"
                  className="left"
                  placeholder="optional note"
                  value={evNote}
                  onChange={e => setEvNote(e.target.value)}
                />
              </td>
              <td>
                <button
                  className="primary"
                  onClick={handleAddEvent}
                  disabled={!canAddEvent}
                  style={{ padding: '4px 10px', fontSize: 'var(--fs-micro)' }}
                >
                  ADD
                </button>
              </td>
            </tr>
          </tbody>
        </table>
          </div>
        ) : null}
      </section>

      {/* OTHER ASSETS (Patch BK)

          Parent card containing the non-Bitcoin asset categories. The
          parent is always expanded (it's where users find the new
          functionality); each sub-card inside is a <details> that
          collapses by default for scannability.

          BK ships Stocks & ETFs and Metals; BL adds Real Estate, Cash,
          and Debt. */}
      <section className="section">
        <p className="section-eyebrow">Other</p>
        <h2 className="section-title">Other</h2>
        <p className="muted" style={{ marginTop: 'calc(-1 * var(--gap-m))', marginBottom: 'var(--gap-m)', lineHeight: 1.6 }}>
          Track non-Bitcoin holdings here so the Dashboard&apos;s Net worth
          picture is complete. Prices auto-refresh via Yahoo for tickers in
          the bundled list; custom symbols are validated on save.
        </p>

        <details style={{ marginTop: 'var(--gap-m)', paddingTop: 'var(--gap-m)', borderTop: '1px solid var(--rule)' }}>
          <summary className="serif" style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: 'var(--fs-lead)',
            fontWeight: 400,
          }}>
            Stocks &amp; ETFs
          </summary>
          <div style={{ marginTop: 'var(--gap-m)' }}>
            <SecuritiesCard
              kindFilter={['stock', 'etf', 'mutual_fund', 'bond_etf', 'commodity_etf', 'crypto_etf']}
              title="Stocks &amp; ETFs"
              customDefaultKind="stock"
              hint="Stocks, non-Bitcoin ETFs, mutual funds, bonds, commodities. Bundled list covers ~200 common Yahoo-resolvable tickers; type any other ticker and we'll validate via Yahoo on save."
            />
          </div>
        </details>

        <details style={{ marginTop: 'var(--gap-m)', paddingTop: 'var(--gap-m)', borderTop: '1px solid var(--rule)' }}>
          <summary className="serif" style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: 'var(--fs-lead)',
            fontWeight: 400,
          }}>
            Metals
          </summary>
          <div style={{ marginTop: 'var(--gap-m)' }}>
            <SecuritiesCard
              kindFilter={['metal_etf', 'spot_metal']}
              title="Metals"
              customDefaultKind="metal_etf"
              hint="Spot metals (gold/silver/platinum/palladium, stored in grams) and metal ETFs (GLD/SLV/etc., stored in shares)."
            />
          </div>
        </details>

        {/* Patch BL: Real Estate, Cash, Debt sub-cards. Each in its own
            <details> disclosure for scannability, same pattern as the
            two BJ sub-cards above. */}
        <details style={{ marginTop: 'var(--gap-m)', paddingTop: 'var(--gap-m)', borderTop: '1px solid var(--rule)' }}>
          <summary className="serif" style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: 'var(--fs-lead)',
            fontWeight: 400,
          }}>
            Real estate
          </summary>
          <div style={{ marginTop: 'var(--gap-m)' }}>
            <RealEstateCard />
          </div>
        </details>

        <details style={{ marginTop: 'var(--gap-m)', paddingTop: 'var(--gap-m)', borderTop: '1px solid var(--rule)' }}>
          <summary className="serif" style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: 'var(--fs-lead)',
            fontWeight: 400,
          }}>
            Cash
          </summary>
          <div style={{ marginTop: 'var(--gap-m)' }}>
            <CashCard />
          </div>
        </details>

      </section>

      <section className="section">
        <p className="section-eyebrow">Liabilities</p>
        <h2 className="section-title">Debt</h2>
        <DebtCard />
      </section>
    </>
  );
}
